import { IFormSchemaField } from "../types";

/**
 * Whether a field is server-owned shared PII / otherwise non-editable. With the
 * forms contract this is signalled by `prefill.editable === false` (the server
 * stamps these values at persist from the live records and rejects them in
 * submit.data). We never send these keys back.
 */
export const isSharedPiiField = (field: IFormSchemaField): boolean =>
  field.prefill?.editable === false;

/**
 * Whether a field's value should be visually masked / excluded from session
 * recording. MaskedId fields (SSN/EIN style) and any field flagged `ui.fsExclude`
 * are sensitive.
 */
export const isSensitiveField = (field: IFormSchemaField): boolean =>
  field.type === "MaskedId" || field.ui?.fsExclude === true;

/**
 * Whether a field is "pre-filled" data the contractor can only verify, not edit
 * — server-owned values stamped from previously collected forms / live records
 * (`prefill.editable === false` with a real prefill SOURCE). These are grouped
 * into the "Pre-filled data" card (rendered read-only via WSDataItem) instead of
 * inline inputs. Excludes server-computed fields AND source-less read-only value
 * fields (e.g. a server-hook-owned worksheet result delivered via prefill.value)
 * — both keep their in-card auto-calculated presentation — and Hidden-UI fields
 * (worksheet intermediates), which render nothing at all.
 */
export const isPrefilledReadOnlyField = (field: IFormSchemaField): boolean =>
  field.prefill?.editable === false &&
  (field.prefill?.source === "DomainContext" ||
    field.prefill?.source === "PreviousFiling") &&
  field.operations.computed == null &&
  field.ui?.component !== "Hidden";

/**
 * A server-owned display value that is NOT identity prefill: read-only with no
 * declared prefill source (the engine surfaces hook-written values this way).
 * Rendered inline as the auto-calculated box, in card position, like computed.
 */
export const isServerOwnedValueField = (field: IFormSchemaField): boolean =>
  field.prefill?.editable === false &&
  field.prefill?.source !== "DomainContext" &&
  field.prefill?.source !== "PreviousFiling" &&
  field.operations.computed == null &&
  field.ui?.component !== "Hidden";

/**
 * Removes the keys of fields matching `shouldStrip`, recursing into repeating
 * Group rows (item fields are full IFormSchemaFields with their own prefill /
 * ui flags, and their row values must be stripped by the same predicate).
 */
const stripFieldKeys = (
  values: Record<string, unknown>,
  fields: IFormSchemaField[],
  shouldStrip: (field: IFormSchemaField) => boolean
): Record<string, unknown> => {
  const cleaned: Record<string, unknown> = { ...values };
  fields.forEach(field => {
    if (shouldStrip(field)) {
      delete cleaned[field.key];
      return;
    }
    const itemFields = field.item?.fields;
    if (field.type === "Group" && itemFields && itemFields.length > 0) {
      const rows = cleaned[field.key];
      if (Array.isArray(rows)) {
        cleaned[field.key] = rows.map(row =>
          row && typeof row === "object"
            ? stripFieldKeys(
                row as Record<string, unknown>,
                itemFields,
                shouldStrip
              )
            : row
        );
      }
    }
  });
  return cleaned;
};

/**
 * Removes non-editable (server-owned) keys from the values payload before submit:
 * the server stamps the shared PII (SSN, legal name, address) from live records and
 * denies those keys in submit.data — including inside repeating Group rows.
 * Editable fields are kept — including the SignatureBlock field, whose value the
 * forms engine validates as Required (so it must remain in `data`; consent is
 * additionally carried in the submit `signature`).
 */
export const stripSharedPiiKeys = (
  values: Record<string, unknown>,
  fields: IFormSchemaField[]
): Record<string, unknown> => stripFieldKeys(values, fields, isSharedPiiField);

/**
 * The draft-persistence filter: on top of the server-owned keys, USER-ENTERED
 * sensitive values (MaskedId / `ui.fsExclude` — e.g. an SSN typed when none is
 * on file) must never be serialised to localStorage in plaintext. The cost is
 * re-typing that one field after a reload, which is right for an SSN.
 */
export const stripDraftExcludedKeys = (
  values: Record<string, unknown>,
  fields: IFormSchemaField[]
): Record<string, unknown> =>
  stripFieldKeys(
    values,
    fields,
    field => isSharedPiiField(field) || isSensitiveField(field)
  );
